import { Layout } from "@/components/layout/Layout";
import { usePageMeta } from "@/hooks/use-page-meta";
import { site } from "@/data/site";
import { Link } from "wouter";

const EFFECTIVE_DATE = "July 16, 2025";
const LAST_UPDATED = "July 16, 2026";

export default function Privacy() {
  usePageMeta(
    "Privacy Policy",
    "Privacy policy for travisjohnson.xyz — how data is collected, used, and protected."
  );

  return (
    <Layout>
      <div className="container mx-auto px-6 max-w-3xl py-12 md:py-20">
        <h1 className="text-4xl md:text-5xl font-serif font-medium tracking-tight mb-4">
          Privacy Policy
        </h1>
        <p className="text-sm text-muted-foreground mb-12">
          Effective: {EFFECTIVE_DATE} · Last updated: {LAST_UPDATED}
        </p>

        <div className="prose prose-lg dark:prose-invert max-w-none space-y-10">

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4 mt-0">1. Overview</h2>
            <p className="text-muted-foreground leading-relaxed">
              This Privacy Policy describes how Travis Johnson ("I," "me," or "my") collects, uses, and
              protects information on <strong>travisjohnson.xyz</strong> (the "Site"). By using this Site
              you agree to the practices described here. If you do not agree, please discontinue use of
              the Site.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              This Site is a personal portfolio and research tool operated by an individual, not a
              corporation. It is not directed at children under 13 and I do not knowingly collect personal
              information from minors.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">2. Information I Collect</h2>

            <h3 className="text-lg font-medium text-foreground mb-2 mt-5">2a. Information You Provide</h3>
            <p className="text-muted-foreground leading-relaxed">
              If you submit the contact form, I collect the following fields you enter voluntarily:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mt-2">
              <li>Full name</li>
              <li>Email address</li>
              <li>Subject line</li>
              <li>Message body</li>
              <li>Company name (optional)</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              This information is used solely to read and respond to your inquiry. It is not sold, shared
              with third parties, or used for marketing without your explicit consent.
            </p>

            <h3 className="text-lg font-medium text-foreground mb-2 mt-5">2b. Automatically Collected Data</h3>
            <p className="text-muted-foreground leading-relaxed">
              Standard web server logs may capture your IP address, browser user-agent, referring URL,
              and the pages you visit, as is typical for any website. This data is used for security,
              uptime monitoring, and diagnosing technical errors only. It is not used for advertising
              profiling and is not retained longer than 30 days.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              I do not deploy invasive analytics platforms, third-party tracking pixels, or behavioral
              advertising cookies on this Site. No cross-site tracking of your browsing activity occurs.
            </p>

            <h3 className="text-lg font-medium text-foreground mb-2 mt-5">2c. Access-Gated Research Tool</h3>
            <p className="text-muted-foreground leading-relaxed">
              A portion of this Site (the private peptide intelligence dashboard at <code className="text-sm bg-muted px-1 rounded">/peptides/</code>)
              is access-restricted and intended solely for my own private research use. If you have been
              granted access, your access-key submission is validated server-side and held only in your
              browser's session storage for the duration of your session. No account data or personal
              profiles are created or stored.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">3. Cookies &amp; Local Storage</h2>
            <p className="text-muted-foreground leading-relaxed">
              This Site uses only functional, first-party browser storage:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mt-2">
              <li><strong>Theme preference</strong> — stores your light/dark mode choice in <code className="text-sm bg-muted px-1 rounded">localStorage</code> so it persists across visits.</li>
              <li><strong>Session access key</strong> — stores a hashed access token in <code className="text-sm bg-muted px-1 rounded">sessionStorage</code> for authenticated areas; cleared when you close the tab.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              No advertising cookies, analytics cookies, or third-party tracking cookies are set. You can
              clear browser storage at any time through your browser settings without affecting any account.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">4. Third-Party Services</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Site integrates or links to the following third-party services. Each has its own privacy
              policy and data practices:
            </p>
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 pr-4 font-medium text-foreground">Service</th>
                    <th className="text-left py-2 pr-4 font-medium text-foreground">Purpose</th>
                    <th className="text-left py-2 font-medium text-foreground">Privacy Policy</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground divide-y divide-border">
                  <tr>
                    <td className="py-2 pr-4">Anthropic Claude</td>
                    <td className="py-2 pr-4">AI-generated research summaries (server-side only)</td>
                    <td className="py-2"><a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer" className="text-foreground hover:underline">anthropic.com/privacy</a></td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">PubMed / NCBI</td>
                    <td className="py-2 pr-4">Scientific literature retrieval (public API)</td>
                    <td className="py-2"><a href="https://www.nlm.nih.gov/web_policies.html" target="_blank" rel="noopener noreferrer" className="text-foreground hover:underline">nlm.nih.gov</a></td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Meta Ad Library API</td>
                    <td className="py-2 pr-4">Ad-creative research (server-side, read-only)</td>
                    <td className="py-2"><a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" className="text-foreground hover:underline">facebook.com/privacy</a></td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">YouTube / Google RSS</td>
                    <td className="py-2 pr-4">Public channel RSS feeds (server-side, no auth)</td>
                    <td className="py-2"><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-foreground hover:underline">policies.google.com</a></td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">FDA.gov</td>
                    <td className="py-2 pr-4">Public press-release RSS feeds (server-side)</td>
                    <td className="py-2"><a href="https://www.fda.gov/about-fda/about-website/fda-website-policies" target="_blank" rel="noopener noreferrer" className="text-foreground hover:underline">fda.gov</a></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-muted-foreground leading-relaxed mt-4">
              All third-party API calls are made server-side. Your IP address and browser identity are not
              forwarded to these services. Data retrieved from these APIs is used solely for private
              research and is not redistributed, resold, or published.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">5. Meta Ad Library API — Specific Disclosure</h2>
            <p className="text-muted-foreground leading-relaxed">
              This Site uses the Meta Ad Library API to retrieve publicly available advertising data for
              private competitive research purposes. Specifically:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>Data retrieved from the API is used solely by me, the site owner, for personal market research.</li>
              <li>No Ad Library data is displayed to the general public, re-published, aggregated for third parties, or used in any commercial product or service.</li>
              <li>No user data from Facebook or Instagram is collected, stored, or processed through this integration.</li>
              <li>The integration accesses only the publicly available Ad Library dataset as permitted by Meta's Platform Terms and Ad Library API Terms of Service.</li>
              <li>All API credentials are stored securely as server-side environment variables and are never exposed client-side.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">6. How I Use Information</h2>
            <p className="text-muted-foreground leading-relaxed">I use the information collected to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mt-2">
              <li>Respond to contact form inquiries</li>
              <li>Diagnose technical errors and maintain site security</li>
              <li>Conduct private market and scientific research (access-gated tool only)</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              I do not use your information for advertising, profiling, automated decision-making, or any
              purpose beyond those listed above.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">7. Data Sharing &amp; Disclosure</h2>
            <p className="text-muted-foreground leading-relaxed">
              I do not sell, rent, or trade personal information. I may disclose information only:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mt-2">
              <li>To comply with a valid legal obligation, court order, or government request;</li>
              <li>To protect the rights, property, or safety of myself or others;</li>
              <li>With service providers who process data on my behalf under confidentiality obligations (e.g., hosting infrastructure).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">8. Data Retention</h2>
            <p className="text-muted-foreground leading-relaxed">
              Contact form submissions are retained only as long as needed to respond to and resolve your
              inquiry, and are deleted upon request. Server logs are purged on a rolling 30-day basis. API
              response caches stored server-side expire within 24 hours by default.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">9. Security</h2>
            <p className="text-muted-foreground leading-relaxed">
              This Site is served over HTTPS. API credentials and secrets are stored as server-side
              environment variables and are never exposed in client-side code or public repositories. While
              I take reasonable measures to protect your information, no method of electronic transmission
              is 100% secure, and I cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">10. Your Rights (California Residents — CCPA)</h2>
            <p className="text-muted-foreground leading-relaxed">
              As a California resident, you have the right to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mt-2">
              <li>Know what personal information is collected and how it is used;</li>
              <li>Request deletion of personal information I hold about you;</li>
              <li>Opt out of the sale of your personal information (I do not sell personal information);</li>
              <li>Non-discrimination for exercising these rights.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              To exercise any of these rights, contact me at the address below.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">11. International Visitors</h2>
            <p className="text-muted-foreground leading-relaxed">
              This Site is operated from {site.location}, United States. If you access it from outside the
              United States, your information may be transferred to and processed in the United States.
              By using the Site, you consent to such transfer.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">12. Changes to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              I may update this policy from time to time. Material changes will be reflected in a new
              "Last updated" date at the top of this page. Continued use of the Site after an update
              constitutes acceptance of the revised policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">13. Contact</h2>
            <p className="text-muted-foreground leading-relaxed">
              For any privacy-related questions, data deletion requests, or concerns, please reach out:
            </p>
            <div className="mt-3 p-4 bg-muted rounded-lg text-muted-foreground text-sm space-y-1">
              <p><strong className="text-foreground">Travis Johnson</strong></p>
              <p>{site.location}</p>
              <p>
                Email: <a href={`mailto:${site.email}`} className="text-foreground hover:underline">{site.email}</a>
              </p>
              <p>
                Web: <Link href="/contact" className="text-foreground hover:underline">travisjohnson.xyz/contact</Link>
              </p>
            </div>
          </section>

          <p className="text-sm pt-8 border-t border-border text-muted-foreground">
            Last updated: {LAST_UPDATED} · <Link href="/terms" className="text-foreground hover:underline">Terms of Use</Link>
          </p>
        </div>
      </div>
    </Layout>
  );
}
